/**
 * Selector de plantillas de email del tenant.
 *
 *   GET /api/v1/email-templates                 → lista de kinds + variants + selección actual
 *   GET /api/v1/email-templates/preview?...     → HTML del preview (lo cargamos en iframe)
 *   PATCH /api/v1/email-templates/selection     → persiste la elección
 *
 * UX:
 *   - Tabs por kind (invitación, confirmación de orden, etc.)
 *   - Por cada variant: card con preview + botón Seleccionar
 *   - Selected variant marcado con check
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Card, Badge, PageHeader, Button } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth, tenantHeaders } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { Mail, Check, Loader2, ExternalLink } from 'lucide-react'

export const Route = createFileRoute('/store/$id/email-templates')({
  component: EmailTemplatesPage,
})

interface VariantInfo {
  variant: string
  label: string
  description: string
}

interface KindInfo {
  kind: string
  label: string
  description: string
  defaultVariant: string
  selectedVariant: string
  variants: VariantInfo[]
}

function EmailTemplatesPage() {
  const qc = useQueryClient()
  const [activeKind, setActiveKind] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => tenantApi.get<{ data: KindInfo[] }>('/api/v1/email-templates'),
  })

  const kinds = data?.data ?? []
  const current = kinds.find((k) => k.kind === activeKind) ?? kinds[0]

  // Auto-seleccionar el primer kind cuando carga
  useEffect(() => {
    if (!activeKind && kinds.length > 0) setActiveKind(kinds[0]!.kind)
  }, [activeKind, kinds])

  const select = useMutation({
    mutationFn: (input: { kind: string; variant: string }) =>
      tenantApi.patch('/api/v1/email-templates/selection', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-templates'] }),
  })

  return (
    <>
      <PageHeader
        title="Plantillas de email"
        description="Elegí el estilo visual de los correos automáticos que tu tienda envía."
      />
      <div className="p-8 space-y-6">
        {isLoading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {error && (
          <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-400">{(error as Error).message}</p>
          </Card>
        )}

        {kinds.length > 0 && (
          <div className="flex gap-2 border-b border-border">
            {kinds.map((k) => (
              <button
                key={k.kind}
                onClick={() => setActiveKind(k.kind)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition',
                  (activeKind ?? kinds[0]?.kind) === k.kind
                    ? 'text-foreground border-blue-500'
                    : 'text-muted-foreground border-transparent hover:text-foreground',
                )}
              >
                <Mail className="size-4" />
                {k.label}
              </button>
            ))}
          </div>
        )}

        {current && (
          <KindEditor
            kind={current}
            onSelect={(variant) => select.mutate({ kind: current.kind, variant })}
            saving={select.isPending}
          />
        )}
      </div>
    </>
  )
}

function KindEditor({
  kind, onSelect, saving,
}: {
  kind: KindInfo
  onSelect: (variant: string) => void
  saving: boolean
}) {
  const [previewVariant, setPreviewVariant] = useState<string>(kind.selectedVariant)

  // Cuando cambia el kind activo, resetear el preview a la selección actual
  useEffect(() => {
    setPreviewVariant(kind.selectedVariant)
  }, [kind.kind, kind.selectedVariant])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
      {/* Lista de variants */}
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{kind.description}</div>
        {kind.variants.map((v) => {
          const isSelected = kind.selectedVariant === v.variant
          const isPreview = previewVariant === v.variant
          return (
            <Card
              key={v.variant}
              className={cn(
                'p-4 cursor-pointer transition',
                isPreview && !isSelected && 'border-blue-500 ring-1 ring-blue-500/30',
                isSelected && 'border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/10',
                !isPreview && !isSelected && 'hover:border-border',
              )}
              onClick={() => setPreviewVariant(v.variant)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{v.label}</p>
                    {isSelected && <Badge variant="success">Activo</Badge>}
                    {kind.defaultVariant === v.variant && !isSelected && <Badge variant="muted">Default</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{v.description}</p>
                </div>
                {isSelected && <Check className="size-5 text-emerald-700 dark:text-emerald-400 shrink-0" />}
              </div>
              {isPreview && !isSelected && (
                <Button
                  size="sm"
                  className="w-full mt-3"
                  onClick={(e) => {
                    e.stopPropagation()
                    onSelect(v.variant)
                  }}
                  disabled={saving}
                >
                  {saving ? <><Loader2 className="size-4 mr-2 animate-spin" /> Guardando...</> : 'Usar esta plantilla'}
                </Button>
              )}
            </Card>
          )
        })}
      </div>

      {/* Preview iframe */}
      <PreviewPane kind={kind.kind} variant={previewVariant} />
    </div>
  )
}

function PreviewPane({ kind, variant }: { kind: string; variant: string }) {
  // DEMO: el preview se genera local desde el seed — no hay backend.
  const [html, setHtml] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [err] = useState<string | null>(null)
  const url = useMemo(() => `#preview/${kind}/${variant}`, [kind, variant])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void import('@/lib/mocks/seeds/email-templates')
      .then(({ previewHtml }) => {
        if (cancelled) return
        setHtml(previewHtml(kind, variant))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [kind, variant])

  return (
    <Card className="p-0 overflow-hidden bg-[hsl(var(--surface))] min-h-[600px] flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-background/50">
        <span className="text-xs font-mono text-muted-foreground">Preview · {variant}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          Abrir en nueva pestaña <ExternalLink className="size-3" />
        </a>
      </div>
      <div className="flex-1 relative bg-white">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex items-center justify-center text-red-700 dark:text-red-400 text-sm p-4">{err}</div>
        )}
        {html && (
          <iframe
            title="Email preview"
            srcDoc={html}
            sandbox="allow-same-origin"
            className="w-full h-[800px] border-0 block"
          />
        )}
      </div>
    </Card>
  )
}
